import { useNavigate, useParams } from 'react-router-dom';
import { PATHS } from 'src/sensors/router.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { Suspense, useCallback, useContext, useEffect, useMemo } from 'react';
import { ConfigContext } from 'src/configContext.ts';
import { useFilterLogic } from 'src/modules/common/hooks/useFilterLogic.ts';
import { skipToken } from '@apollo/client';
import { MainLayout } from 'src/modules/common/layouts/MainLayout.tsx';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer.tsx';
import { NavigationButton } from 'src/modules/common/components/Navigation/NavigationButton.tsx';
import { BreadcrumbContainer } from 'src/common/Breadcrumb/BreadcrumbContainer.tsx';
import { LocationStateContextType } from 'src/sensors/locationStateContext.ts';
import { RuntimeContext } from 'src/runtimeContext';
import { isRecommendedAccessory } from 'src/utils/isRecommendedAccessory';
import { sortAccessories } from 'src/modules/common/utils/sortAccessories.ts';
import { Breadcrumbs } from 'src/sensors/Breadcrumbs.ts';
import { SkeletonPageLayout } from 'src/modules/common/skeletons/SkeletonPageLayout/SkeletonPageLayout';
import { NoData } from 'src/modules/common/components/NoData/NoData';
import { Query } from 'src/modules/common/components/Query/Query';
import {
    getSensorsAccessoriesPageConfig, SensorsAccessoriesPageData,
    SensorsAccessoriesPageVariables,
} from 'src/sensors/accessories/SensorsAccessoriesPageMapping.ts';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { composeFilter } from 'src/utils/composeFilter.ts';
import { AccessoryGrid } from 'src/common/Accessory/AccessoryGrid';
import { Accessory } from 'src/common/Accessory/Accessory';
import { useLocationState } from 'src/sensors/useLocationState.ts';

export const SensorAccessoriesPage = () => {
    const {
        sensorLabel,
        page,
    } = useParams();

    if (!sensorLabel) {
        throw new Error('No Sensor selected');
    }
    if (!page) {
        throw new Error('No page provided');
    }

    const [locationState, setLocationState] = useLocationState();
    const { isMobile } = useContext(RuntimeContext);
    useEffect(() => {
        if (locationState.sensorLabel !== sensorLabel) {
            setLocationState({ ...locationState, sensorLabel });
        }
    }, [locationState, setLocationState, sensorLabel, page]);

    const { userSettings, mandatoryFilterConfig } = useContext(ConfigContext);

    const pageConfig = useMemo(() => getSensorsAccessoriesPageConfig(userSettings.currentRegion), [userSettings]);
    if (pageConfig === null) {
        throw new Error('No Page Config!');
    }

    const onChange = useCallback((label: string) => {
        let selectedAccessories: LocationStateContextType['locationState']['selectedAccessories'] = [];
        if (locationState.selectedAccessories.find((item) => item.label === label) !== undefined) {
            selectedAccessories = locationState.selectedAccessories.filter((item) => item.label !== label);
        } else {
            selectedAccessories = [...locationState.selectedAccessories, { label, qty: 1 }];
        }
        setLocationState({
            ...locationState,
            selectedAccessories,
        });
    }, [locationState, setLocationState]);

    const [
        searchParams,
        filter,
    ] = useFilterLogic(pageConfig.filterConfig);

    const navigate = useNavigate();

    const breadcrumbsProps: Breadcrumbs = [
        {
            title: MESSAGES.SelectedSensor,
            value: locationState.sensorLabel,
            state: locationState,
            to: {
                pathname: [
                    PATHS.Sensors,
                    locationState.sensorsPage,
                ].join('/'),
                search: searchParams.toString(),
            },
        },
        {
            title: MESSAGES.SelectedAccessories,
            selected: true,
            disabled: true,
            value: locationState.selectedAccessories.length > 0
                ? locationState.selectedAccessories.map(({ label }) => label).join(', ') : '-',
            actionShown: locationState.selectedAccessories.length > 0,
            onActionClick: () => {
                setLocationState({
                    ...locationState,
                    selectedAccessories: [],
                });
            },
            to: '#',
        },
    ];

    const navigation = (
        <NavigationContainer isMobile={isMobile}>
            <NavigationButton
                type="back"
                state={locationState}
                to={{
                    pathname: [
                        PATHS.Sensors,
                        page,
                    ].join('/'),
                    search: searchParams.toString(),
                }}
            >
                <I18n messageId={MESSAGES.back}/>
            </NavigationButton>

            <NavigationButton
                type="forward"
                state={locationState}
                to={{
                    pathname: [
                        PATHS.Checkout,
                        encodeURIComponent(sensorLabel),
                        page,
                    ].join('/'),
                    search: searchParams.toString(),
                }}
            >
                <I18n messageId={MESSAGES.next}/>
            </NavigationButton>
        </NavigationContainer>
    );

    const variables = useMemo((): {
        variables: SensorsAccessoriesPageVariables;
    } | typeof skipToken => {
        if (filter === null) {
            return skipToken;
        }
        return {
            variables: {
                filter: composeFilter(
                    mandatoryFilterConfig.config,
                    mandatoryFilterConfig.values,
                    userSettings.currentLocale,
                ),
                label: sensorLabel,
                locale: userSettings.currentLocale,
            },
        };
    }, [
        mandatoryFilterConfig,
        filter,
        sensorLabel,
        userSettings,
    ]);

    const renderAccessory = (item: SensorsAccessoriesPageData['accessories']['accessories'][0]['item']) => (
        <Accessory
            chip={isRecommendedAccessory(item.zOption)
                ? <I18n messageId={MESSAGES.recommended}/> : null}
            accessory={item}
            onSelect={() => onChange(item.Label)}
            selected={locationState.selectedAccessories.find(({ label }) => label === item.Label) !== undefined}
            key={`${item.Label}-${item.ID}`}
        />
    );

    return (
        <div className="vss-sensors">
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.accessoriesPageTitle}
                    navigation={isMobile ? null : navigation}
                    breadcrumbs={(
                        <BreadcrumbContainer
                            isMobile={isMobile}
                            breadcrumbs={breadcrumbsProps}
                            containerWrapperClass="vss-sensors-breadcrumbs"
                        />
                    )}
                />

                <Suspense fallback={<SkeletonPageLayout/>}>
                    <Query<SensorsAccessoriesPageData, SensorsAccessoriesPageVariables>
                        query={pageConfig.query}
                        variables={variables}
                        onResult={(data) => {
                            if (!data) {
                                return;
                            }
                            const { accessories } = data;
                            const total = (accessories.accessories.length || 0);
                            if (total === 0) {
                                navigate({
                                    pathname: [
                                        PATHS.Checkout,
                                        encodeURIComponent(sensorLabel),
                                        page,
                                    ].join('/'),
                                    search: searchParams.toString(),
                                }, { state: locationState });
                            }
                        }}
                    >
                        {({ data }) => {
                            if (data === undefined) {
                                return (
                                    <SkeletonPageLayout/>
                                );
                            }

                            if (data.accessories.accessories.length === 0) {
                                return <NoData/>;
                            }

                            return (
                                <div className="vss-accessories-page">
                                    {data.accessories.accessories && (
                                        <AccessoryGrid title={MESSAGES.accessoriesPageSensorsTitle}>
                                            {data.accessories.accessories
                                                .slice()
                                                .sort(sortAccessories)
                                                .map(
                                                    ({ item }) => renderAccessory(item),
                                                )}
                                        </AccessoryGrid>
                                    )}
                                </div>
                            );
                        }}
                    </Query>
                </Suspense>
            </MainLayout>

            {isMobile && navigation}
        </div>
    );
};
